import { m } from '@sohanscript/i18n';
import { useScrollTransform } from '@sohanscript/lib/hooks/scroll-transform';
import { Motion } from '@sohanscript/ui/motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/trpc/client';
import { ProjectCard } from './project-card';

const AUTO_SPEED = 0.003;
const LERP = 0.06;
const N_RING = 13;

type Layout = { radius: number; perspective: string; dragPxPerRad: number };

export function ProjectSlider() {
	const [projects] = api.project.homepage.useSuspenseQuery();
	const n = projects.length;

	const [ringN, setRingN] = useState(N_RING);
	useEffect(() => {
		const updateRingN = () => {
			const width = window.innerWidth;
			setRingN(width < 480 ? 7 : width < 1024 ? 9 : N_RING);
		};
		updateRingN();
		window.addEventListener('resize', updateRingN);
		return () => window.removeEventListener('resize', updateRingN);
	}, []);

	const ringCount = Math.min(n, ringN);
	const ringStep = (2 * Math.PI) / ringCount;

	const angle = useRef(0);
	const targetAngle = useRef(0);
	const isDragging = useRef(false);
	const isPaused = useRef(false);
	const isMobile = useRef(false);
	const dragOrigin = useRef({ x: 0, startAngle: 0 });
	const resumeTimer = useRef<ReturnType<typeof setTimeout>>(null);
	const stepsRef = useRef(0);

	const ringRef = useRef<HTMLDivElement>(null);
	const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

	const [steps, setSteps] = useState(0);
	const [layout, setLayout] = useState<Layout>({
		radius: 400,
		perspective: '1600px',
		dragPxPerRad: 320,
	});

	const activeSlot = ((steps % ringCount) + ringCount) % ringCount;

	useEffect(() => {
		const update = () => {
			const el = cardRefs.current[0];
			if (!el) return;
			const cardWidth = el.offsetWidth;
			if (!cardWidth) return;
			const width = window.innerWidth;
			const mobile = width < 1024;
			isMobile.current = mobile;
			const radius = Math.round(cardWidth / 2 / Math.tan(Math.PI / ringCount));
			const dragPxPerRad = Math.max(180, Math.round(width / 3));
			setLayout({
				radius,
				perspective: `${radius * 4}px`,
				dragPxPerRad,
			});
		};
		const raf = requestAnimationFrame(update);
		window.addEventListener('resize', update);
		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener('resize', update);
		};
	}, [ringCount]);

	useEffect(() => {
		let raf: number;
		const tick = () => {
			if (!isPaused.current && !isDragging.current)
				targetAngle.current -= AUTO_SPEED;
			angle.current += (targetAngle.current - angle.current) * LERP;
			const a = angle.current;
			if (ringRef.current) ringRef.current.style.transform = `rotateY(${a}rad)`;
			const newSteps = Math.round(-a / ringStep);
			if (newSteps !== stepsRef.current) {
				stepsRef.current = newSteps;
				setSteps(newSteps);
			}
			const mobile = isMobile.current;
			const fadeStart = mobile ? ringStep * 0.4 : ringStep * 2.0;
			const fadeRange = mobile ? ringStep * 0.5 : ringStep * 0.6;
			for (let i = 0; i < ringCount; i++) {
				const el = cardRefs.current[i];
				if (!el) continue;
				const rel =
					(((i * ringStep + a) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
				const abs = rel > Math.PI ? 2 * Math.PI - rel : rel;
				const opacity =
					abs <= fadeStart
						? 1
						: Math.max(0.12, 1 - (abs - fadeStart) / fadeRange);
				el.style.opacity = String(opacity);
				el.style.pointerEvents = opacity <= 0.12 ? 'none' : 'auto';
			}
			raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [ringCount, ringStep]);

	const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
		isDragging.current = true;
		isPaused.current = true;
		dragOrigin.current = { x: e.clientX, startAngle: angle.current };
		e.currentTarget.setPointerCapture(e.pointerId);
		clearTimeout(resumeTimer.current ?? 0);
	}, []);

	const onPointerMove = useCallback(
		(e: React.PointerEvent) => {
			if (!isDragging.current) return;
			const a =
				dragOrigin.current.startAngle +
				(e.clientX - dragOrigin.current.x) / layout.dragPxPerRad;
			angle.current = a;
			targetAngle.current = a;
		},
		[layout.dragPxPerRad],
	);

	const onPointerUp = useCallback(() => {
		if (!isDragging.current) return;
		isDragging.current = false;
		targetAngle.current = Math.round(angle.current / ringStep) * ringStep;
		resumeTimer.current = setTimeout(() => {
			isPaused.current = false;
		}, 2000);
	}, [ringStep]);

	const target = React.useRef(null);
	const transformedValue = useScrollTransform({
		target,
		output: { translateZ: [-300, 0] },
	});

	return (
		<div
			style={{
				perspective: '1000px',
			}}
			className="flex flex-col items-center"
			ref={target}
		>
			<Motion
				style={transformedValue.output}
				className="relative h-96 lg:h-130 w-screen overflow-hidden"
			>
				<div
					className="absolute inset-0 cursor-grab select-none active:cursor-grabbing"
					style={{
						perspective: layout.perspective,
						perspectiveOrigin: '50% 50%',
						touchAction: 'none',
					}}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerUp}
					onMouseEnter={() => {
						if (!isDragging.current) isPaused.current = true;
					}}
					onMouseLeave={() => {
						if (!isDragging.current) isPaused.current = false;
					}}
				>
					<div
						ref={ringRef}
						className="absolute inset-0 flex items-center justify-center"
						style={{ transformStyle: 'preserve-3d' }}
					>
						{Array.from({ length: ringCount }, (_, i) => i).map((i) => {
							const k = (i - activeSlot + ringCount) % ringCount;
							const signedK =
								k <= Math.floor(ringCount / 2) ? k : k - ringCount;
							const projectIdx = (((steps + signedK) % n) + n) % n;
							const project = projects[projectIdx]!;
							return (
								<div
									key={i}
									ref={(el) => {
										cardRefs.current[i] = el;
									}}
									className="isolate -mx-1 w-56 sm:w-64 md:w-72"
									style={{
										position: 'absolute',
										transform: `rotateY(${i * ringStep}rad) translateZ(${layout.radius}px)`,
									}}
								>
									<ProjectCard {...project} isActive={i === activeSlot} />
								</div>
							);
						})}
					</div>
				</div>
			</Motion>
			<Button icon="jam:task-list" variant="hover" href="/works">
				{m.pc_show_more()}
			</Button>
		</div>
	);
}
